/**
 * The V2 surface, minus the socket wiring.
 *
 * It is a SEPARATE component from `index.tsx` for two reasons:
 *  1. the design lab renders exactly this, inside its own frame, with a fake
 *     socket — so what the lab shows is the real screen, not a copy;
 *  2. it keeps the screen root free of everything but the hooks, so a
 *     SocketProvider state change re-renders the root and nothing else.
 *
 * It also OWNS THE MEASUREMENT: `onLayout` on the root gives the real content
 * box (the app header ~118 px and the icon drawer ~108 px are outside it), and
 * that box — not `window.innerWidth` — decides the viewport, the rail width
 * and the board size. Measuring here rather than in `index.tsx` is what makes
 * the lab's fluid desktop frame behave exactly like the real screen.
 *
 * Layout (UX spec §2, amended): the pre-game states are PAGES (no Stage); the
 * game states are Stage + Rail, placed by `layouts/DesktopLayout` or
 * `layouts/PhoneLayout`. Body decides WHICH layout and hands it the elements;
 * the layouts read no store, so the elements they receive are created here — in
 * a component that does not re-render on a move.
 */
import React, {Suspense, useCallback} from 'react';
import {StyleSheet, View} from 'react-native';

import {useLiveGame} from '@/Store/liveGameStore';
import {selectRailState} from '@/Store/liveGame/selectors';

import Strip from './Strip';
import {PAGE_STATES} from './chrome/pageStates';
import DesktopLayout from './layouts/DesktopLayout';
import PhoneLayout from './layouts/PhoneLayout';
import ResultOverlay from './overlay/ResultOverlay';

/**
 * The old `ShimmerLoader`'s import closure reaches `@/navigators/StackConstants`
 * (= every screen, this one included). Required on FIRST RENDER instead of
 * imported, so webpack evaluates it after the Play screen's own module graph is
 * complete — a static import made a module cycle that left the rail's exports
 * undefined (crash 2026-10-05). Unlike `React.lazy`, a `require` is synchronous,
 * so the tests that render this arm keep working.
 *
 * Fix G, 2026-10-06 removed the SAME pattern's other user, the tournament page
 * (`pages/tournament`, which rendered the old `TournamentPlay` by import): the
 * tournament is the ordinary Stage + Rail now, like a live game, and
 * `TournamentPlay` is no longer imported anywhere in this tree — the eagerGraph
 * guard's `rail/tournament/panels/` scoped-tsc noise it used to drag along with
 * it is gone too.
 */
let ShimmerLoaderCached: React.ComponentType<any> | null = null;
function ShimmerLoader(props: any) {
  if (!ShimmerLoaderCached) {
    ShimmerLoaderCached =
      require('@/Screens/PlayChess/components/ShimmerLoader').default;
  }
  const Shimmer = ShimmerLoaderCached as React.ComponentType<any>;
  return <Shimmer {...props} />;
}
import Rail from './rail/Rail';
import Stage from './stage/Stage';
import {bump} from './stage/renderCount';
import {SPACE} from './theme/tokens';
import {useV2Theme} from './theme/useV2Theme';
import {LAYOUT, useV2Ui} from './theme/useV2Ui';

// the lobby VARIANT switch (M2 §1): it picks `classic` or `gallery` once and
// lazy-loads only that arm
const PlayHomePage = React.lazy(() => import('./pages/home'));

function PlayChessV2Body() {
  bump('Body');
  const railState = useLiveGame(selectRailState);
  /**
   * E7: the BOOTSTRAP. Until the socket is up and the handshake has told us who
   * we are, the lobby has nothing to draw and used to render an empty page for
   * the (sub-second, but visible) round trip. `ShimmerLoader` is the old
   * screen's own placeholder, reused by import, exactly as `PlayChessSwitch`
   * does while it resolves the lane. Both flags: a reconnect mid-session keeps
   * `me`, and the lobby is perfectly usable then.
   */
  const booting = useLiveGame(s => !s.session.connected && !s.session.me);
  const viewport = useV2Ui(s => s.viewport);
  const railWidth = useV2Ui(s => s.railWidth);
  const tokens = useV2Theme(s => s.tokens);

  // react-native-web backs onLayout with a ResizeObserver, so this fires on
  // mount AND on every window/drawer resize — no window listener needed.
  const onLayout = useCallback((event: any) => {
    const {width, height} = event.nativeEvent.layout;
    useV2Ui.getState().setLayout(Math.round(width), Math.round(height));
  }, []);

  const isPage = PAGE_STATES.includes(railState);
  const phone = viewport === 'phone';

  const stage = <Stage />;
  const overlay = <ResultOverlay />;
  const rail = <Rail railState={railState} />;

  return (
    <View
      style={[styles.root, {backgroundColor: tokens.bg}]}
      onLayout={onLayout}>
      {/* The Strip is in flow and takes 0 px while there is nothing to say.
          When it appears it pushes the page down rather than resizing the
          board — the board must never change size mid-game (UX spec §2.1).
          It stays here rather than inside a layout because the PAGE states
          need it too. */}
      <View style={[styles.stripSlot, phone && styles.stripSlotPhone]}>
        <Strip />
      </View>

      {isPage && booting ? (
        /* E7: the bootstrap placeholder — and the lazy lobby chunk is not even
           requested until there is a session to draw it for. */
        <ShimmerLoader isLoading={true}>
          <View style={styles.root} />
        </ShimmerLoader>
      ) : isPage ? (
        <Suspense fallback={<View style={styles.root} />}>
          {/* 'seeking' is the same PAGE as 'home': the seek card
              (the variant's seek card) and the CTA are leaves with their own
              subscriptions, so the page itself never swaps. */}
          <PlayHomePage />
        </Suspense>
      ) : phone ? (
        <PhoneLayout stage={stage} overlay={overlay} rail={rail} />
      ) : (
        <DesktopLayout
          stage={stage}
          overlay={overlay}
          rail={rail}
          railWidth={railWidth}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  stripSlot: {
    paddingHorizontal: LAYOUT.pagePadding,
  },
  stripSlotPhone: {
    paddingHorizontal: SPACE.md,
    paddingTop: 6,
  },
});

export default React.memo(PlayChessV2Body);
